import type { ComponentType, ReactNode } from 'react';
import { Route, Navigate } from 'react-router-dom';
import { LessonsPortalGuard } from '@/components/member/LessonsPortalGuard';
import { ClubMemberPortalGuard } from '@/components/member/ClubMemberPortalGuard';
import LessonDetail from '@/pages/dashboards/student/lessons/LessonDetail';
import BookLesson from '@/pages/dashboards/student/bookings/BookLesson';
import MyCalendar from '@/pages/dashboards/student/bookings/MyCalendar';
import MyBookings from '@/pages/dashboards/student/bookings/MyBookings';
import StudentCreditsPage from '@/pages/dashboards/student/credits/StudentCreditsPage';
import MyStudents from '@/pages/dashboards/student/profile/MyStudents';
import Settings from '@/pages/shared/Settings';
import Notifications from '@/pages/shared/Notifications';
import MemberMembershipPage from '@/pages/dashboards/member/MemberMembershipPage';
import MemberNoticesPage from '@/pages/dashboards/member/MemberNoticesPage';
import MemberMyMatchesPage from '@/pages/dashboards/member/MemberMyMatchesPage';
import { PublicOrgConsumerPortalPage } from '@/components/layout/PublicOrgConsumerPortalPage';
import { OrgInventoryHiresRedirect } from '@/components/public/org/OrgInventoryHiresRedirect';
import { OrgClubPaymentsRedirect } from '@/components/public/org/OrgClubPaymentsRedirect';

function lessonsPortalPage(
  title: string,
  description: string | undefined,
  Page: ComponentType
): ReactNode {
  return (
    <PublicOrgConsumerPortalPage title={title} description={description}>
      <LessonsPortalGuard>
        <Page />
      </LessonsPortalGuard>
    </PublicOrgConsumerPortalPage>
  );
}

/** Shared authenticated consumer routes inside the public org shell. */
export function memberPortalChildRoutes(prefix = '') {
  const p = (path: string) => {
    if (!prefix) {
      // Relative segments when nested under /club-academy/:orgSlug (or subdomain root).
      return path.startsWith('/') ? path.slice(1) : path;
    }
    return `${prefix}${path}`;
  };

  return (
    <>
      {/* Redirect targets resolve against the live URL so the :orgSlug pattern is not navigated to literally. */}
      <Route path={p('/dashboard')} element={<Navigate to=".." relative="path" replace />} />
      <Route path={p('/profile')} element={<Navigate to="../settings" relative="path" replace />} />
      <Route
        path={p('/settings')}
        element={
          <PublicOrgConsumerPortalPage
            title="Settings"
            description="Manage your account, security, and preferences"
          >
            <Settings />
          </PublicOrgConsumerPortalPage>
        }
      />
      <Route
        path={p('/notifications')}
        element={
          <PublicOrgConsumerPortalPage
            title="Notifications"
            description="View and manage your in-app notifications"
          >
            <Notifications />
          </PublicOrgConsumerPortalPage>
        }
      />

      <Route
        path={p('/lesson/:id')}
        element={
          <PublicOrgConsumerPortalPage title="Lesson details">
            <LessonsPortalGuard>
              <LessonDetail />
            </LessonsPortalGuard>
          </PublicOrgConsumerPortalPage>
        }
      />
      <Route
        path={p('/book-lesson/:lessonId')}
        element={
          <PublicOrgConsumerPortalPage title="Book lesson">
            <LessonsPortalGuard>
              <BookLesson />
            </LessonsPortalGuard>
          </PublicOrgConsumerPortalPage>
        }
      />
      <Route path={p('/calendar')} element={lessonsPortalPage('My Calendar', 'Monthly and weekly views of your lesson times', MyCalendar)} />
      <Route path={p('/bookings')} element={lessonsPortalPage('My Bookings', 'Current and past enrollments', MyBookings)} />
      <Route
        path={p('/schedule')}
        element={
          <LessonsPortalGuard>
            <Navigate to="../calendar" relative="path" replace />
          </LessonsPortalGuard>
        }
      />
      <Route
        path={p('/credits')}
        element={lessonsPortalPage('Make-up Credits', 'Manage your earned credits and book make-up lessons', StudentCreditsPage)}
      />
      <Route
        path={p('/payments')}
        element={<Navigate to="../bookings" relative="path" replace />}
      />
      <Route
        path={p('/my-students')}
        element={lessonsPortalPage('My Students', 'View your profile and any students you have added', MyStudents)}
      />

      <Route
        path={p('/membership')}
        element={
          <ClubMemberPortalGuard feature="memberships">
            <MemberMembershipPage />
          </ClubMemberPortalGuard>
        }
      />
      <Route path={p('/inventory-hires')} element={<OrgInventoryHiresRedirect />} />
      <Route path={p('/club-payments')} element={<OrgClubPaymentsRedirect />} />
      <Route
        path={p('/notices')}
        element={
          <ClubMemberPortalGuard>
            <MemberNoticesPage />
          </ClubMemberPortalGuard>
        }
      />
      <Route
        path={p('/play/my-matches')}
        element={<MemberMyMatchesPage />}
      />
    </>
  );
}
